import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "redux/hooks"
import { showAlert } from "redux/features/notice/noticeSlice"
import { makeConsent } from "redux/features/consent/consentThunk"
import { formatMoney } from "helpers/formatters"
import {
  Container,
  Content,
  Title,
  Amount,
  Pay,
  Terms,
  Description,
} from "components/Subscription/Modal/Create/elements"
import InputCheckbox from "components/ui/InputCheckbox"
import {
  SUBSCRIPTION_BANK_METHOD,
  SUBSCRIPTION_CARD_METHOD,
  // TODO: uncomment when zlick will be returned
  // SUBSCRIPTION_MOBILE_METHOD,
  SUBSCRIPTION_CONSENT_TYPE,
  PLAN_PERIOD_MONTH,
} from "config"
import {
  PlanOptionType,
  SubscriptionMethodType,
  SubscriptionValues,
} from "types"

type Props = {
  failure: boolean
  plan: PlanOptionType
  onSubmit: (values: SubscriptionValues) => void
}

const SubscriptionProviders = ({ failure, plan, onSubmit }: Props) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const [isSubmitting, setSubmitting] = useState(false)
  const [consentAccepted, acceptConsent] = useState(false)

  const handleToggleConsent = () => {
    acceptConsent(!consentAccepted)
  }

  const isMonth = plan?.value === PLAN_PERIOD_MONTH

  const SUBSCRIPTION_METHODS = isMonth
    ? [
        // TODO: uncomment when zlick will be returned
        // {
        //   label: t(
        //     `features.subscription.methods.${SUBSCRIPTION_MOBILE_METHOD}.title`,
        //   ),
        //   value: SUBSCRIPTION_MOBILE_METHOD,
        // },
        {
          label: t(
            `features.subscription.methods.${SUBSCRIPTION_CARD_METHOD}.title`,
          ),
          value: SUBSCRIPTION_CARD_METHOD,
        },
        {
          label: t(
            `features.subscription.methods.${SUBSCRIPTION_BANK_METHOD}.title`,
          ),
          value: SUBSCRIPTION_BANK_METHOD,
        },
      ]
    : [
        {
          label: t(
            `features.subscription.methods.${SUBSCRIPTION_CARD_METHOD}.title`,
          ),
          value: SUBSCRIPTION_CARD_METHOD,
        },
        {
          label: t(
            `features.subscription.methods.${SUBSCRIPTION_BANK_METHOD}.title`,
          ),
          value: SUBSCRIPTION_BANK_METHOD,
        },
      ]

  const handleClick = (value: string) => {
    if (!consentAccepted) {
      dispatch(
        showAlert({
          message: t("notice.subscription.consent.error"),
        }),
      )

      return false
    } else {
      setSubmitting(true)

      dispatch(makeConsent({ consent_type: SUBSCRIPTION_CONSENT_TYPE }))
        .then(() => {
          onSubmit({ plan_id: plan.id, service_type: value })
        })
        .catch((error) => {
          dispatch(showAlert({ message: error }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    }
  }

  return (
    <Content>
      <Title variant="h4">
        {t("features.subscription.steps.method.title")}
      </Title>
      <Description variant="h6">
        {t("features.subscription.steps.method.description")}
      </Description>
      {plan && plan?.price && (
        <Amount variant="h1">{formatMoney(plan.price)}</Amount>
      )}
      <Container
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        {SUBSCRIPTION_METHODS.map(
          ({ label, value }: SubscriptionMethodType, index: number) => (
            <Pay
              key={index}
              disabled={isSubmitting}
              onClick={() => handleClick(value)}
            >
              {label}
            </Pay>
          ),
        )}
        <Terms>
          <InputCheckbox
            label={t("features.subscription.steps.consent.title")}
            value={consentAccepted}
            onChange={handleToggleConsent}
          />
        </Terms>
      </Container>
      {failure && (
        <Description variant="h6">
          {t("features.subscription.steps.method.failure")}
        </Description>
      )}
    </Content>
  )
}

SubscriptionProviders.defaultProps = {
  failure: false,
}

export default SubscriptionProviders
